import { graphHooks } from '@tunasong/graph-lib/react'
import { shortUuid } from '@tunasong/models'
import { useGlobalAppContext, usePlugins } from '@tunasong/plugin-lib'
import { useNavigateToEntity } from '@tunasong/ui-lib'
import { useState } from 'react'

/**
 * The Home page's create actions: the header's New song split button and the first-run start tiles.
 *
 * New items land at the root, which Home lists as Unfiled; the user can move them into a space later.
 */
export const useHomeCreate = () => {
  const plugins = usePlugins('all')
  const globalContext = useGlobalAppContext()
  const navigateToEntity = useNavigateToEntity()
  const graph = graphHooks.useEntityGraph()
  // Cached-graph create (also populates the graph cache)
  const { createEntity } = graphHooks.useEntityCreate()
  const [busy, setBusy] = useState(false)

  /**
   * Mirrors the CreateEntityDialog flow (apps/tunasong/tunasong-ui/src/entity/create-entity-dialog.tsx) so the
   * new entity is fully scaffolded: the plugin template provides the seed content, the id is set up front,
   * template.onCreate runs (a band space creates its calendar there), and navigation prefers the plugin's
   * `Edit` layout so the user lands in the editor.
   */
  const createFromTemplate = async (type: string, name: string) => {
    const plugin = plugins?.find(p => p.node?.type === type)
    const template = plugin?.templates?.[0]
    if (!plugin || !template) {
      return
    }
    const newEntity = { ...template.template, id: shortUuid(), name }
    setBusy(true)
    try {
      // Resolves once the server has stored the entity, so the navigation below finds its collab room (#1895)
      const entity = await createEntity({ parent: null, entity: newEntity })
      if (!entity) {
        return
      }
      const layouts = Object.keys(plugin.layouts ?? {})
      const layout = layouts.includes('Edit') ? 'Edit' : undefined
      navigateToEntity(entity, { layout })
      if (template.onCreate) {
        // Fire-and-forget: the entity view is already mounting; onCreate side effects land while the user reads.
        void template.onCreate({ entity, parent: undefined, globalContext })
      }
    } finally {
      setBusy(false)
    }
  }

  const createSpace = async (name = 'New Space') => {
    setBusy(true)
    try {
      const space = await graph
        .createEntity({ parent: null, entity: { type: 'folder', name, tags: ['space'] } })
        .unwrap()
      if (space) {
        navigateToEntity(space)
      }
    } finally {
      setBusy(false)
    }
  }

  return { createFromTemplate, createSpace, busy }
}
